/**
 * page_view enrichment (ONESTORE-298).
 *
 * `application_name` / `product_area` / `product_section` come from Squidex, but
 * the page_view event is pushed from `onRouteUpdate` in `gatsby-browser.js`,
 * which only receives the location — it has no access to page data. React
 * components therefore hand their values over through a global object.
 *
 * A dedicated global is used rather than `window.onestoreContext`, because
 * `getStoreContext` spreads that one into the order API payload.
 *
 * Writers must use a layout effect: `onRouteUpdate` runs from
 * `RouteUpdates.componentDidMount`/`componentDidUpdate`, a parent of the page,
 * so a passive `useEffect` is flushed only after the event has been pushed.
 *
 * Generic-page values are stored together with the url they belong to, so a
 * value left behind by a previously visited page can never leak into the event
 * of a page that has none.
 */

export type PageAnalyticsValues = {
  productArea: string | null
  productSection: string | null
}

export type PageAnalyticsCustomUrl = PageAnalyticsValues & {
  url: string
}

export type PageAnalyticsConfiguration = {
  applicationName: string | null
  customUrls: PageAnalyticsCustomUrl[]
}

type GenericPageAnalytics = PageAnalyticsValues & {
  url: string
}

type PageAnalyticsContext = {
  configuration?: PageAnalyticsConfiguration
  genericPage?: GenericPageAnalytics
}

declare global {
  interface Window {
    onestorePageAnalytics?: PageAnalyticsContext
  }
}

export type ResolvedPageAnalytics = PageAnalyticsValues & {
  applicationName: string | null
}

/**
 * Squidex stores urls without a trailing slash while Gatsby serves pages with
 * one (`trailingSlash: "always"`), so both sides are normalized before matching.
 *
 * Query strings and hashes are not part of the identity of a page here, so they
 * are dropped — `location.pathname` never carries them, and a configured url
 * that was pasted with one still matches.
 */
export function normalizePath(path: string): string {
  const pathname = path.split(/[?#]/)[0]

  return pathname.replace(/\/+$/, "") || "/"
}

export function setPageAnalyticsConfiguration(
  configuration: PageAnalyticsConfiguration
): void {
  window.onestorePageAnalytics = {
    ...window.onestorePageAnalytics,
    configuration,
  }
}

export function setGenericPageAnalytics(
  genericPage: GenericPageAnalytics
): void {
  window.onestorePageAnalytics = {
    ...window.onestorePageAnalytics,
    genericPage,
  }
}

/**
 * Resolves the values for the page currently being viewed. Generic pages carry
 * their own values; every other url is looked up in the Squidex configuration.
 *
 * `null` is returned for missing values — the Jentis SDK maps them to "not set"
 * and `pushLocationChange` does the same for GA4.
 */
export function resolvePageAnalytics(pathname: string): ResolvedPageAnalytics {
  const { configuration, genericPage } = window.onestorePageAnalytics ?? {}

  const currentPath = normalizePath(pathname)

  const values =
    genericPage && normalizePath(genericPage.url) === currentPath
      ? genericPage
      : configuration?.customUrls?.find(
          (customUrl) => normalizePath(customUrl.url) === currentPath
        )

  return {
    applicationName: configuration?.applicationName ?? null,
    productArea: values?.productArea ?? null,
    productSection: values?.productSection ?? null,
  }
}
